'use client';

/**
 * CanadaFooterLegal — the "Important Message" block passed to EditorialFooter's
 * `legal` prop on the Canada landing page.
 *
 * The shared EditorialFooter default states that offerings are NOT made
 * available to Canadian residents, which is the opposite of this page. This
 * block is the handoff's Canadian replacement: Belco's registration, the
 * no-offering-without-documents language, and the StartEngine-group /
 * Belco separation. Copy is verbatim from the handoff.
 */
import { styled } from '@mui/material/styles';
import { FONTS } from 'components/HomepageV3/theme/tokens';
import { BELCO_EMAIL, BELCO_MAILTO, CONTACT_EMAIL, PRIVACY_EMAIL } from '../data/canadaContent';

const LegalH = styled('h4')({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'rgba(233,215,182,0.8)',
  margin: '0 0 14px',
});

const LegalSub = styled('h4')({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'rgba(233,215,182,0.7)',
  margin: '26px 0 12px',
});

const LegalList = styled('ul')({
  margin: '0 0 16px',
  paddingLeft: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export function CanadaFooterLegal() {
  return (
    <>
      <LegalH>Important Message</LegalH>
      <p>
        In making an investment decision, investors must rely on their own examination of the issuer
        and the terms of the offering, including the merits and risks involved. Investments made
        available on this website are speculative, illiquid, and involve a high degree of risk,
        including the possible loss of your entire investment.
      </p>
      <p>
        Belco Private Capital Inc. has been retained as the portfolio manager, investment fund
        manager and exempt market dealer to provide StartEngine and related and connected issuers
        with all services requiring such registrations in Canada. For offering documents and
        subscription information, and to discuss whether a StartEngine investment available in
        Canada is suitable for you, please contact Warren Aarons, a registered dealing
        representative with Belco, at <a href={BELCO_MAILTO}>{BELCO_EMAIL}</a>.
      </p>
      <p>
        Under no circumstances should this information be construed as an offering of any securities
        for sale directly or indirectly in any province or territory of Canada. Any offering is made
        only pursuant to the relevant offering documents together with the relevant subscription
        agreement, both of which should be read in their entirety. No offer to sell securities will
        be made prior to receipt of these documents by the offeree, and no offer to purchase
        securities will be accepted prior to completion of all appropriate documentation. Neither
        the Ontario Securities Commission nor any other securities regulatory authority of any
        Canadian jurisdiction has passed upon the accuracy or adequacy of this information material,
        and any representation to the contrary is unlawful. Results may differ materially from those
        described herein, and there may be factors and risks that cause actions, events or results
        not to be as anticipated, estimated or intended. You should not place undue reliance on past
        performance or forward-looking information contained herein.
      </p>
      <p>
        <a href="https://www.startengine.com">www.StartEngine.com</a> is a website owned and
        operated by StartEngine Crowdfunding, Inc. (&ldquo;StartEngine&rdquo;), which is neither a
        registered broker-dealer, investment advisor nor funding portal.
      </p>
      <p>
        Unless indicated otherwise with respect to a particular issuer, all securities-related
        activity in the United States is conducted by regulated affiliates of StartEngine:
        StartEngine Capital LLC, a funding portal{' '}
        <a href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&company=startengine+capital&type=&dateb=&owner=include&count=40">
          registered here with the US Securities and Exchange Commission (SEC)
        </a>{' '}
        and{' '}
        <a href="https://www.finra.org/about/funding-portals-we-regulate">
          here as a member of the Financial Industry Regulatory Authority (FINRA)
        </a>
        , or StartEngine Primary LLC (&ldquo;SE Primary&rdquo;), a broker-dealer registered with the
        SEC and FINRA / SIPC. You can review the background of our broker-dealer and our investment
        professionals on{' '}
        <a href="https://brokercheck.finra.org/firm/summary/291387">
          FINRA&apos;s BrokerCheck here
        </a>
        . StartEngine Secondary is an alternative trading system (ATS) regulated by the SEC and
        operated by SE Primary. SE Primary is a member of SIPC and explanatory brochures are
        available upon request by contacting SIPC at (202) 371-8300.
      </p>
      <p>StartEngine facilitates three types of primary offerings in the United States:</p>
      <LegalList>
        <li>
          Regulation A offerings (JOBS Act Title IV; known as Regulation A+), which are offered to
          non-accredited and accredited investors alike. These offerings are made through
          StartEngine Primary, LLC (unless otherwise indicated).
        </li>
        <li>
          Regulation D offerings (Rule 506(c)), which are offered only to accredited investors.
          These offerings are made through StartEngine Primary, LLC.
        </li>
        <li>
          Regulation Crowdfunding offerings (JOBS Act Title III), which are offered to
          non-accredited and accredited investors alike. These offerings are made through
          StartEngine Primary LLC.
        </li>
      </LegalList>
      <p>
        Any securities offered on this website have not been recommended or approved by any federal
        or state securities commission or regulatory authority. StartEngine and its affiliates do
        not provide any investment advice or recommendation and do not provide any legal or tax
        advice concerning any securities.
      </p>
      <p>
        Neither StartEngine nor any of its officers, directors, agents, and employees makes any
        warranty, express or implied, of any kind whatsoever related to the adequacy, accuracy, or
        completeness of any information on this site or the use of information on this site.
      </p>
      <LegalSub>The StartEngine Group of Companies</LegalSub>
      <p>
        Unless otherwise specified, references to &ldquo;StartEngine&rdquo; on this website are
        intended to mean the StartEngine group of companies. The firms that comprise the StartEngine
        group of companies include StartEngine Primary, LLC. Each entity provides specific services
        in a particular geographic area and is subject to the laws and professional regulations of
        the particular country or countries in which it operates. Each firm enters into client
        engagements independently. No advice is intended to be rendered, nor is any advice provided,
        by a StartEngine company unless a client service agreement is in place. In Canada,
        StartEngine has retained Belco Private Capital Inc. (&ldquo;Belco&rdquo;) to perform
        investment fund manager, portfolio manager and exempt market dealer services.
      </p>
      <p>
        StartEngine firms are separate and distinct legal entities, which cannot obligate each
        other. Belco is an independent contractor and also separate and distinct from the
        StartEngine group of companies. Each StartEngine company and Belco is liable only for its
        own acts or omissions and not those of each other. Each StartEngine entity is structured
        differently in accordance with national laws, regulations, customary practice, and other
        factors, and may secure the provision of professional services through subsidiaries,
        affiliates, and/or other entities.
      </p>
      <p>
        StartEngine Crowdfunding, Inc. is a U.S.-based company and is not registered to trade in or
        advise on securities in any Canadian jurisdiction. For greater certainty, StartEngine
        Primary, LLC&apos;s services are only offered to residents based in the United States.
        Conversely, Belco Private Capital Inc.&apos;s services are only offered to residents based
        in Canada.
      </p>
      <p>
        Investing in private company securities is not suitable for all investors. An investment in
        private company securities is highly speculative and involves a high degree of risk. It
        should only be considered a long-term, illiquid investment. You must be prepared to
        withstand a total loss of your investment. Past performance and forward-looking information
        do not predict future results.
      </p>
      <p>
        By accessing this site and any pages on this site, you agree to be bound by our{' '}
        <a href="#canada-terms">Terms of Use</a> and <a href="#canada-privacy">Privacy Policy</a>,
        as may be amended from time to time without notice or liability. For information about how
        personal information is collected, used, and disclosed, please review our{' '}
        <a href="#canada-privacy">Privacy Policy</a> or contact us at{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
      </p>
      <LegalSub>StartEngine Marketplace</LegalSub>
      <p>
        StartEngine Marketplace (&ldquo;SE Marketplace&rdquo;) is a website operated by StartEngine
        Primary, LLC (&ldquo;SE Primary&rdquo;), a broker-dealer that is registered with the SEC and
        a member of FINRA and the SIPC.
      </p>
      <p>
        StartEngine Secondary (&ldquo;SE Secondary&rdquo;) is our investor trading platform. SE
        Secondary is an SEC-registered Alternative Trading System (&ldquo;ATS&rdquo;) operated by SE
        Primary that matches orders for buyers and sellers of securities.
      </p>
      <p>
        Even if a security is qualified to be displayed on SE Marketplace, there is no guarantee an
        active trading market for the securities will ever develop, or if developed, be maintained.
        You should assume that you may not be able to liquidate your investment for some time or be
        able to pledge these shares as collateral.
      </p>
      <p>
        Certain offerings and features described on StartEngine&apos;s U.S. website are not
        available to Canadian residents. Investment opportunities available to Canadian residents
        are offered only through Belco Private Capital Inc. as investment fund manager, portfolio
        manager and exempt market dealer.
      </p>
      <p>
        California Investors Only &ndash; Do Not Sell My Personal Information (800-317-2200).
        StartEngine does not sell personal information. For all customer inquiries, please write to{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </>
  );
}

export default CanadaFooterLegal;
